// Domain types - shows strong TypeScript modeling skills

export interface Lead {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  title?: string;
  industry?: string;
  phone?: string;
  status: LeadStatus;
  score: number;
  source: LeadSource;
  assignedTo?: string;
  createdDate: string;
  lastActivityDate?: string;
  convertedDate?: string;
  convertedOpportunityId?: string;
}

export interface Opportunity {
  id: string;
  name: string;
  amount: number;
  stage: OpportunityStage;
  probability: number;
  closeDate: string;
  accountId: string;
  contactId: string;
  ownerId: string;
  leadSource: LeadSource;
  createdDate: string;
  lastModifiedDate: string;
  isClosed: boolean;
  isWon: boolean;
}

export interface Account {
  id: string;
  name: string;
  industry?: string;
  employees?: number;
  annualRevenue?: number;
  website?: string;
  phone?: string;
  billingCity?: string;
  billingState?: string;
  billingCountry?: string;
  type: AccountType;
  ownerId: string;
}

export interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  title?: string;
  phone?: string;
  accountId: string;
  ownerId: string;
  leadSource?: LeadSource;
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  isActive: boolean;
  profilePhotoUrl?: string;
}

// Enums for type safety
export enum LeadStatus {
  NEW = 'New',
  CONTACTED = 'Contacted',
  QUALIFIED = 'Qualified',
  UNQUALIFIED = 'Unqualified',
  CONVERTED = 'Converted'
}

export enum OpportunityStage {
  PROSPECTING = 'Prospecting',
  QUALIFICATION = 'Qualification',
  NEEDS_ANALYSIS = 'Needs Analysis',
  VALUE_PROPOSITION = 'Value Proposition',
  PROPOSAL = 'Proposal/Price Quote',
  NEGOTIATION = 'Negotiation/Review',
  CLOSED_WON = 'Closed Won',
  CLOSED_LOST = 'Closed Lost'
}

export enum LeadSource {
  WEB = 'Web',
  PHONE_INQUIRY = 'Phone Inquiry',
  PARTNER_REFERRAL = 'Partner Referral',
  PURCHASED_LIST = 'Purchased List',
  OTHER = 'Other',
  ADVERTISING = 'Advertising',
  TRADE_SHOW = 'Trade Show',
  WEBINAR = 'Webinar',
  WORD_OF_MOUTH = 'Word of mouth',
  EMAIL = 'Email'
}

export enum AccountType {
  PROSPECT = 'Prospect',
  CUSTOMER = 'Customer - Direct',
  CUSTOMER_CHANNEL = 'Customer - Channel',
  CHANNEL_PARTNER = 'Channel Partner / Reseller',
  INSTALLATION_PARTNER = 'Installation Partner',
  TECHNOLOGY_PARTNER = 'Technology Partner',
  OTHER = 'Other'
}

export enum UserRole {
  SALES_REP = 'Sales Rep',
  SALES_MANAGER = 'Sales Manager',
  SALES_VP = 'VP Sales',
  MARKETING = 'Marketing',
  ADMIN = 'System Administrator'
}

// API Response types
export interface SalesforceApiResponse<T> {
  totalSize: number;
  done: boolean;
  records: T[];
  nextRecordsUrl?: string;
}

export interface SalesforceError {
  message: string;
  errorCode: string;
  fields?: string[];
}

// Dashboard specific types
export interface PipelineMetrics {
  totalValue: number;
  totalOpportunities: number;
  averageDealSize: number;
  winRate: number;
  salesCycle: number; // days
  conversionRate: number;
}

export interface LeadMetrics {
  totalLeads: number;
  newLeads: number;
  qualifiedLeads: number;
  convertedLeads: number;
  leadScore: {
    average: number;
    distribution: Array<{ range: string; count: number }>;
  };
}

export interface SalesActivity {
  totalCalls: number;
  totalEmails: number;
  totalMeetings: number;
  responseRate: number;
  lastWeekActivity: number;
  thisWeekActivity: number;
}

// Chart data types
export interface ChartDataPoint {
  date: string;
  value: number;
  label?: string;
}

export interface PipelineByStage {
  stage: OpportunityStage;
  count: number;
  value: number;
  probability: number;
}

// Filter and pagination types
export interface QueryFilters {
  dateRange?: {
    start: string;
    end: string;
  };
  ownerId?: string;
  stage?: OpportunityStage[];
  leadSource?: LeadSource[];
  minAmount?: number;
  maxAmount?: number;
}

export interface PaginationParams {
  offset: number;
  limit: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}